import React from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Box } from "@mui/material";
import { Button } from "components/haven-design/buttons";
import { Modal } from "components/haven-design/modal";
import { DialogActions, DialogContent } from "components/haven-design/modal/dialog";
import { getApolloErrorMessage } from "utils/apollo";
import {
  ElasticQuery,
  bulkSelectionOwners,
  bulkSelectionOwnersVariables,
  removeInterestOwnersBulk,
  removeInterestOwnersBulkVariables,
} from "types/gql-types";
import {
  BULK_SELECTION_OWNERS,
  REMOVE_INTEREST_OWNERS_BULK,
} from "pages/dashboard/interests-profile/components/actions-query";
import OwnerSelect, { OwnerOption } from "components/common/owners-editor/owner-select";
import { ownerLabel, ownerNameFrom } from "components/common/owners-editor/owner-names";
import BulkResultSummary from "./bulk-result-summary";
import {
  BulkOwnerError,
  BulkOwnerProgress,
  BulkOwnerResult,
  useBulkOwnerAction,
} from "./bulk-owner-feedback";
import { countOf } from "./utils";

const joinNames = (names: string[]): string => {
  if (names.length <= 2) return names.join(" and ");
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
};

const confirmationLabel = (chosen: OwnerOption[], count: number) => (chosen.length === 0
  ? `Choose the owners to remove from ${countOf(count, "application")}.`
  : `Remove ${joinNames(chosen.map((option) => option.label))} from ${countOf(count, "application")}? Other owners are kept.`);

const OPTIONS_ERROR_FALLBACK = "We couldn't load the owners of the selected applications.";

export type RemoveOwnerModalProps = {
  isOpen: boolean;
  onRequestClose: () => void;
  onCancel: () => void;

  /** Number of selected applicants — shown in the confirmation message. */
  selectedCount: number;
  interestListIds: string[];
  interestIds: string[] | null;
  elasticQuery: ElasticQuery | null;
  pageFrom: number | null;
  pageTo: number | null;

  /**
   * Called after a remove with its result; the caller decides between the
   * all-success toast/refresh and leaving a partial failure to the summary.
   */
  onSuccess?: (result: BulkOwnerResult) => void;
};

const RemoveOwnerModal: React.FC<RemoveOwnerModalProps> = ({
  isOpen,
  onRequestClose,
  onCancel,
  selectedCount,
  interestListIds,
  interestIds,
  elasticQuery,
  pageFrom,
  pageTo,
  onSuccess,
}) => {
  // Same selection variables as the removal, so the picker lists exactly the
  // owners the removal can reach — select-all and page ranges included.
  const { data, loading: ownersLoading, error: ownersError } = useQuery<
  bulkSelectionOwners,
  bulkSelectionOwnersVariables
  >(BULK_SELECTION_OWNERS, {
    variables: { interestListIds, interestIds, elasticQuery, pageFrom, pageTo },
    skip: !isOpen,
    // Owners change underneath the dialog (other bulk actions, other users).
    fetchPolicy: "network-only",
  });

  const [removeOwners, { loading: isSubmitting }] = useMutation<
  removeInterestOwnersBulk,
  removeInterestOwnersBulkVariables
  >(REMOVE_INTEREST_OWNERS_BULK);

  const {
    selectedOwners,
    setSelectedOwners,
    failureResult,
    errorMessage,
    submit,
    retry,
    close,
    cancel,
  } = useBulkOwnerAction({
    mutate: (variables) => removeOwners({ variables })
      .then((res) => res.data?.removeInterestOwnersBulk),
    isSubmitting,
    interestListIds,
    scope: { interestIds, elasticQuery, pageFrom, pageTo },
    onResult: onSuccess,
    onRequestClose,
    onCancel,
  });

  // Server order (display name) is kept; the list is not grouped by property.
  const options = React.useMemo<OwnerOption[]>(
    () => (data?.bulkSelectionOwners ?? []).map(({ id, profile }) => ({
      userId: id,
      label: ownerLabel(ownerNameFrom(profile.firstName, profile.lastName)),
      group: "",
      isMe: false,
    })),
    [data],
  );

  const hasNoOwners = Boolean(data) && !ownersLoading && !ownersError && options.length === 0;

  if (failureResult) {
    return (
      <Modal title="Remove Owner" isOpen={isOpen} onRequestClose={close} fullWidth>
        <BulkResultSummary
          result={failureResult}
          onRetry={retry}
          onClose={close}
          isRetrying={isSubmitting}
          error={errorMessage && <BulkOwnerError id="remove-owner-error" message={errorMessage} />}
        />
      </Modal>
    );
  }

  const renderPicker = () => {
    if (ownersError) {
      return (
        <BulkOwnerError
          id="remove-owner-options-error"
          message={getApolloErrorMessage(ownersError, OPTIONS_ERROR_FALLBACK)}
        />
      );
    }
    if (hasNoOwners) {
      return (
        <Box component="p" id="remove-owner-empty" data-cy="remove-owner-empty">
          None of the selected applications have an owner.
        </Box>
      );
    }
    return (
      <OwnerSelect
        multiple
        id="remove-owner-select"
        grouped={false}
        options={options}
        loading={ownersLoading}
        value={selectedOwners}
        disabled={isSubmitting}
        onChange={setSelectedOwners}
      />
    );
  };

  return (
    <Modal title="Remove Owner" isOpen={isOpen} onRequestClose={close} fullWidth>
      <DialogContent>
        {!hasNoOwners && (
          <Box component="p" id="remove-owner-confirmation">
            {confirmationLabel(selectedOwners, selectedCount)}
          </Box>
        )}
        {renderPicker()}
        {isSubmitting && (
          <BulkOwnerProgress id="remove-owner-progress" message="Removing owners…" />
        )}
        {errorMessage && <BulkOwnerError id="remove-owner-error" message={errorMessage} />}
      </DialogContent>
      <DialogActions>
        <Button
          color="secondary"
          size="S"
          onClick={cancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          color="dangerous"
          size="S"
          marginLeft
          onClick={submit}
          disabled={
            isSubmitting || ownersLoading || Boolean(ownersError) || selectedOwners.length === 0
          }
        >
          {isSubmitting ? "Submitting" : "Remove"}
        </Button>
      </DialogActions>
    </Modal>
  );
};

export default RemoveOwnerModal;
